import type { InternalAxiosRequestConfig } from 'axios';

import { isOnPremRuntime } from '@/utils/http/onPremOptionalMetadataHeaders';

/**
 * Paths where the on-prem app serves PUT and PATCH from *different* handlers.
 * `main_onprem.py` aliases PUT onto every PATCH-only route at the composition
 * root (MOD-1507), but deliberately skips paths that already register a
 * genuine PUT — so on these, a rewritten PATCH reaches the full-replace
 * handler and drops every field the partial body omits.
 *
 * Mirrors `KNOWN_PUT_PATCH_COLLISIONS` in ml-scribe's
 * `tests/unit/test_main_onprem.py` — extend both sides together. Matched
 * against the client-relative path, so the `open-api/` variants of these
 * routes are covered by the same patterns.
 */
const PUT_PATCH_DIVERGENT_ROUTES: readonly RegExp[] = [
  /(^|\/)access-entitlements\/users\/[^/]+$/,
  /(^|\/)sessions\/[^/]+\/consult-note:link-section$/,
];

/**
 * Axios request interceptor: rewrite PATCH to PUT on the on-prem desktop
 * runtime, where hospital TLS-inspecting proxies strip PATCH from the CORS
 * preflight allow-methods. The on-prem backend answers PUT on every PATCH
 * route from the same `APIRoute` object, so handler and partial-merge
 * semantics are preserved everywhere except the divergent paths above.
 *
 * No-op everywhere else — cloud and on-prem *web* are same-origin and keep
 * PATCH.
 */
export const rewriteOnPremPatchToPutAlias = <T extends InternalAxiosRequestConfig>(
  config: T
): T => {
  if (!isOnPremRuntime()) return config;
  if ((config.method ?? '').toLowerCase() !== 'patch') return config;

  const path = (config.url ?? '').split('?')[0] ?? '';
  if (PUT_PATCH_DIVERGENT_ROUTES.some((route) => route.test(path))) return config;

  config.method = 'put';
  return config;
};
